import { useNavigate, Link } from 'react-router-dom';
import { trackFunnel } from '../utils/funnel';
import { useAuth } from '../hooks/useAuth';
import { useEffect, useState } from 'react';
import { AppHeader } from '../components/AppHeader';
import { analytics } from '../utils/analytics';

const AUTH_URL = import.meta.env.VITE_AUTH_SERVER || 'http://localhost:8787';

// Feature flag: testimonials section is hidden until real quotes are collected.
// Flip SHOW_TESTIMONIALS to true AND populate TESTIMONIALS with real quotes before
// launching. Do not fabricate; fake testimonials damage credibility more than their
// absence. Founder-led outreach for 3-5 real quotes is the recommended path.
const SHOW_TESTIMONIALS = false;
const TESTIMONIALS: { quote: string; author: string; role?: string }[] = [
  // { quote: "Got three interview calls in the first week.", author: "K. Morales", role: "Senior PM" },
];

export function LandingPage() {
  const navigate = useNavigate();
  const { isSignedIn, loading } = useAuth();
  const [weeklyCount, setWeeklyCount] = useState<number | null>(null);

  // If already signed in, go straight to the builder
  // The only record that someone from a given channel ever saw this page.
  // Fires once per mount, before anything they might click.
  useEffect(() => {
    trackFunnel('landing_view');
  }, []);

  useEffect(() => {
    if (!loading && isSignedIn) navigate('/app', { replace: true });
  }, [isSignedIn, loading, navigate]);

  // QR-code attribution. URLs printed on QR codes look like
  // `https://draftresume.app/?qr=<label>`. When a visitor lands here with
  // that param: log a qr_scan event server-side, persist the label in
  // localStorage so it survives the OTP sign-in roundtrip, and strip the
  // param from the URL so refresh / share doesn't re-fire it. Visitor is
  // NOT forced to sign up - they just browse normally and we attribute
  // them later if they convert.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const qr = params.get('qr');
    if (!qr) return;
    const code = qr.toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 64);
    if (!code) return;
    try { localStorage.setItem('dr_qr_source', code); } catch { /* quota / private mode */ }
    fetch(`${AUTH_URL}/auth/qr/scan`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    }).catch(() => { /* fire-and-forget */ });
    // Strip ?qr= from the address bar so reload / share doesn't double-count.
    params.delete('qr');
    const next = window.location.pathname + (params.toString() ? '?' + params.toString() : '') + window.location.hash;
    window.history.replaceState({}, '', next);
  }, []);

  // Weekly resume-count stat (cached server-side for 1 hour)
  useEffect(() => {
    fetch(`${AUTH_URL}/public/stats`)
      .then(r => r.ok ? r.json() : null)
      .then((d: { resumes_last_week?: number } | null) => {
        if (d && typeof d.resumes_last_week === 'number' && d.resumes_last_week > 0) {
          setWeeklyCount(d.resumes_last_week);
        }
      })
      .catch(() => { /* silent: landing page works without this */ });
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <AppHeader />

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 pt-20 pb-16 text-center">
        <p className="text-sm font-medium text-orange-600 mb-4 tracking-wide uppercase">AI resume writer. You keep control.</p>
        <h1 className="text-5xl md:text-6xl font-extrabold text-stone-900 leading-tight mb-6">
          A resume rewritten for the job,<br />
          <span className="text-orange-600">in 30 seconds.</span>
        </h1>
        <p className="text-xl text-stone-500 max-w-2xl mx-auto mb-10">
          Paste your resume, paste the job, and DraftResume rewrites it for that role.
          You approve every change. Export to PDF or Word.
        </p>
        <div className="flex items-center justify-center gap-4 flex-wrap">
          <Link
            to="/app"
            onClick={() => analytics.ctaClick('hero')}
            className="bg-stone-900 text-white text-lg px-8 py-4 rounded-xl hover:bg-stone-800 transition-colors font-bold shadow-lg hover:shadow-xl"
          >
            Try it free
          </Link>
          <a
            href="#how-it-works"
            className="text-lg text-stone-500 hover:text-stone-700 px-4 py-4"
          >
            See how it works
          </a>
        </div>
        <p className="text-sm text-stone-400 mt-4">3 free passes. No credit card. No signup needed to try.</p>
        {weeklyCount !== null && (
          <p className="text-sm text-stone-500 mt-3">
            <span className="font-semibold text-stone-700">{weeklyCount.toLocaleString()}</span> resumes tailored in the last week
          </p>
        )}
      </section>

      {/* Research strip: 2025 labor-market data, not competitor marketing */}
      <section className="border-y border-stone-200 bg-stone-900 text-stone-100 py-10">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-xl sm:text-2xl font-medium leading-snug">
            In 2024, only 17% of job applicants made it to an interview. Nearly 60% of recruiters now rely on AI to source and screen candidates.
          </p>
          <p className="text-xs text-stone-400 mt-3">
            The Josh Bersin Company, {' '}
            <a
              href="https://joshbersin.com/talent-acquisition-revolution/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline decoration-stone-500 hover:decoration-stone-200"
            >
              &ldquo;The Talent Acquisition Revolution: How AI is Transforming Recruiting&rdquo;
            </a>
            , September 2025. Analysis of 2024 application and hiring data in partnership with AMS.
          </p>
        </div>
      </section>

      {/* Key differentiator bar: what makes us different, not ATS fear */}
      <section className="border-b border-stone-200 bg-stone-50 py-8">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-lg font-semibold text-stone-800 mb-2">Your resume, rewritten. Not replaced.</p>
          <p className="text-sm text-stone-500 mb-4">The AI drafts. You decide. No lock-in, no template jail, no account required to try.</p>
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-stone-500">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-orange-600" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
              Paste text or upload PDF
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-orange-600" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
              Click to edit any line
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-orange-600" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
              Cover letter included
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-orange-600" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
              Export PDF or Word
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="max-w-6xl mx-auto px-4 py-20">
        <h2 className="text-3xl font-bold text-center text-stone-900 mb-3">Control, not takeover</h2>
        <p className="text-center text-stone-500 mb-12 max-w-xl mx-auto">Three ways to get a rewrite, from &ldquo;redo the whole thing&rdquo; to &ldquo;fix this one bullet.&rdquo;</p>
        <div className="grid md:grid-cols-3 gap-8">
          <div className="text-center p-6">
            <div className="w-14 h-14 bg-orange-100 text-orange-700 rounded-xl flex items-center justify-center mx-auto mb-4 text-2xl font-bold">1</div>
            <h3 className="text-lg font-semibold text-stone-900 mb-2">Full rewrite</h3>
            <p className="text-stone-500">Paste your resume and a job posting. The AI rewrites the whole thing for that role, in your voice. Ready to export in 30 seconds.</p>
          </div>
          <div className="text-center p-6">
            <div className="w-14 h-14 bg-stone-200 text-stone-800 rounded-xl flex items-center justify-center mx-auto mb-4 text-2xl font-bold">2</div>
            <h3 className="text-lg font-semibold text-stone-900 mb-2">Section by section</h3>
            <p className="text-stone-500">Pick the sections you want changed. Leave the rest untouched. Review and accept each change individually.</p>
          </div>
          <div className="text-center p-6">
            <div className="w-14 h-14 bg-orange-100 text-orange-700 rounded-xl flex items-center justify-center mx-auto mb-4 text-2xl font-bold">3</div>
            <h3 className="text-lg font-semibold text-stone-900 mb-2">Bullet by bullet</h3>
            <p className="text-stone-500">Click any bullet or paragraph. The AI rewrites just that piece. Accept or revert with one click.</p>
          </div>
        </div>
      </section>

      {/* Testimonials: gated by SHOW_TESTIMONIALS flag at the top of this file */}
      {SHOW_TESTIMONIALS && TESTIMONIALS.length > 0 && (
        <section id="testimonials" className="max-w-6xl mx-auto px-4 py-16">
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-gray-900 mb-10">What people are saying</h2>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {TESTIMONIALS.map((t, i) => (
              <figure key={i} className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                <blockquote className="text-gray-700 leading-relaxed mb-4">&ldquo;{t.quote}&rdquo;</blockquote>
                <figcaption className="text-sm text-gray-500">
                  <span className="font-semibold text-gray-900">{t.author}</span>
                  {t.role && <span> · {t.role}</span>}
                </figcaption>
              </figure>
            ))}
          </div>
        </section>
      )}

      {/* Features: lead with what we do, not what keyword-scoring tools do */}
      <section className="bg-stone-50 py-20">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-stone-900 mb-3">What you get</h2>
          <p className="text-center text-stone-500 mb-12 max-w-xl mx-auto">Not a scoring tool. Not a template catalog. A working draft, every time.</p>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
              { title: 'Your resume, your data', desc: 'Your resume lives in your browser. We send it to the AI to tailor it, and it is only saved to an account if you press Save. Never sold, never shared with advertisers.' },
              { title: 'No passwords, ever', desc: "We don't store passwords because we never ask for one. Sign-in is a one-time code emailed to you - so the only way into your account is the inbox you already protect. Fewer secrets for us to leak, and nothing for an attacker to phish or reuse from another breach. The strongest auth surface is the smallest one." },
              { title: 'A real draft in 5 minutes', desc: 'The AI rewrites the actual content for the role. Not just keyword suggestions you still have to write yourself.' },
              { title: 'Cover letter from the same flow', desc: 'Once your resume is tailored, generate a matching cover letter with one click. Three tone options. Edit and export.' },
              { title: 'Click any line to edit it', desc: 'The preview is editable. Tweak the AI\'s phrasing, fix a date, reword a bullet. Your final word is literally final.' },
              { title: 'Custom "vibe" templates', desc: 'Describe a style ("dark mode", "warm teacher", "retro typewriter") and the AI designs a one-off template around your content. Four built-in designs too.' },
              { title: 'Pay per draft', desc: 'No subscription trap. $1 = 1 tailored resume. 10 for $5. Tokens never expire. Monthly plan only if you want it.' },
            ].map(f => (
              <div key={f.title} className="bg-white rounded-xl p-6 border border-stone-200">
                <h3 className="font-semibold text-stone-900 mb-1">{f.title}</h3>
                <p className="text-sm text-stone-500">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Before / After demo: same career rewritten for a specific role, no keyword-highlight cue.
          Every fact in "after" also appears in "before" - reworded, reordered, or reframed in the
          job's language, never added. That is the literal promise this page makes a few lines up
          ("won't invent"), so the demo has to be provably honest, not just look impressive. */}
      <section id="before-after" className="max-w-6xl mx-auto px-4 py-16 sm:py-20">
        <h2 className="text-2xl sm:text-3xl font-bold text-center text-stone-900 mb-3">What a rewrite actually looks like</h2>
        <p className="text-center text-stone-500 mb-10 text-sm sm:text-base max-w-2xl mx-auto">
          Same career. Same facts. Two resumes, because the job on the right is a specific React role and the one on the left is &ldquo;looking for work.&rdquo;
        </p>
        <div className="grid md:grid-cols-2 gap-4 max-w-5xl mx-auto">
          {/* Before */}
          <div className="relative">
            <div className="absolute -top-3 left-4 bg-stone-100 border border-stone-200 text-stone-600 text-xs font-semibold px-3 py-1 rounded-full">What you pasted</div>
            <div className="bg-white border border-stone-200 rounded-xl p-5 sm:p-6 font-mono text-[11px] sm:text-xs text-stone-600 leading-relaxed h-full">
              <p className="font-bold text-stone-900 text-sm mb-1">Alex Rivera</p>
              <p className="text-stone-500 mb-3">Software Engineer · alex@email.com</p>
              <p className="font-semibold text-stone-800 mt-3 mb-1">Summary</p>
              <p>Experienced software engineer with a background in web applications. Passionate about clean code and learning new technologies.</p>
              <p className="font-semibold text-stone-800 mt-3 mb-1">Experience</p>
              <p className="font-semibold text-stone-700">Software Engineer, Acme Corp</p>
              <ul className="list-disc pl-5 mt-1 space-y-1">
                <li>Built and maintained features for Acme's main product using React and TypeScript</li>
                <li>Reviewed pull requests and mentored two junior developers</li>
                <li>Rebuilt the team's internal deploy tool, cutting release time from about a day to under an hour</li>
              </ul>
            </div>
          </div>
          {/* After */}
          <div className="relative">
            <div className="absolute -top-3 left-4 bg-stone-900 text-white text-xs font-semibold px-3 py-1 rounded-full shadow">Rewritten for: Senior React Engineer</div>
            <div className="bg-white border-2 border-stone-900 rounded-xl p-5 sm:p-6 text-[11px] sm:text-xs text-stone-700 leading-relaxed h-full shadow-md">
              <p className="font-bold text-stone-900 text-base mb-1">Alex Rivera</p>
              <p className="text-stone-500 mb-3 text-xs">Software Engineer · React · TypeScript · alex@email.com</p>
              <p className="font-semibold text-orange-700 uppercase tracking-wide text-[10px] mt-3 mb-1">Summary</p>
              <p>Software engineer with hands-on experience building React and TypeScript applications. Skilled in code review, mentoring, and improving team tooling and release process.</p>
              <p className="font-semibold text-orange-700 uppercase tracking-wide text-[10px] mt-3 mb-1">Experience</p>
              <p className="font-semibold text-stone-900">Software Engineer · Acme Corp</p>
              <ul className="list-disc pl-5 mt-1 space-y-1">
                <li>Built and maintained React and TypeScript features for Acme's main product</li>
                <li>Rebuilt the team's internal deploy tool, cutting release time from about a day to under an hour</li>
                <li>Reviewed pull requests and mentored two junior developers</li>
              </ul>
            </div>
          </div>
        </div>
        <p className="text-center text-xs text-stone-400 mt-4">Same person, same facts, same title. The rewrite reorders and reframes what&rsquo;s already there for this specific role - it doesn&rsquo;t add years, numbers, or a promotion the original didn&rsquo;t have.</p>
      </section>

      {/* Pricing */}
      <section id="pricing" className="max-w-6xl mx-auto px-4 py-20">
        <h2 className="text-3xl font-bold text-center text-gray-900 mb-4">Pay per draft. No subscription trap.</h2>
        <p className="text-center text-gray-500 mb-12">1 token = one rewritten resume. Tokens never expire. Monthly plan only if you want it.</p>
        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto items-stretch">
          <div className="border border-stone-200 rounded-xl p-6 text-center bg-white flex flex-col">
            <div className="text-sm font-medium text-stone-500 mb-2">Starter</div>
            <div className="text-4xl font-bold text-stone-900 mb-1">Free</div>
            <div className="text-sm text-stone-400 mb-6">3 tokens included</div>
            <ul className="text-sm text-stone-600 space-y-2 mb-6 text-left flex-grow">
              <li>3 full resume tailoring passes</li>
              <li>Or 30 inline edits</li>
              <li>All 3 tailoring modes</li>
              <li>PDF and Word export</li>
            </ul>
            <Link
              to="/app"
              className="w-full border border-stone-300 text-stone-700 py-2.5 rounded-lg hover:bg-stone-50 font-medium min-h-[44px] mt-auto text-center inline-flex items-center justify-center"
            >
              Get started
            </Link>
          </div>

          <div className="border-2 border-stone-900 rounded-xl p-6 text-center relative bg-white flex flex-col">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-stone-900 text-white text-xs px-3 py-1 rounded-full font-medium">Most popular</div>
            <div className="text-sm font-medium text-stone-500 mb-2">10 Pack</div>
            <div className="text-4xl font-bold text-stone-900 mb-1">$5</div>
            <div className="text-sm text-stone-400 mb-6">10 tokens</div>
            <ul className="text-sm text-stone-600 space-y-2 mb-6 text-left flex-grow">
              <li>10 full resume passes</li>
              <li>Or 100 inline edits</li>
              <li>Never expires</li>
              <li>Referral bonuses</li>
            </ul>
            <Link
              to="/login"
              className="w-full bg-stone-900 text-white py-2.5 rounded-lg hover:bg-stone-800 font-medium min-h-[44px] mt-auto text-center inline-flex items-center justify-center"
            >
              Buy tokens
            </Link>
          </div>

          <div className="border-2 border-orange-500 rounded-xl p-6 text-center relative bg-white flex flex-col">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-orange-600 text-white text-xs px-3 py-1 rounded-full font-medium">For active searches</div>
            <div className="text-sm font-medium text-stone-500 mb-2">Monthly</div>
            <div className="text-4xl font-bold text-stone-900 mb-1">$20<span className="text-lg font-normal text-stone-400">/mo</span></div>
            <div className="text-sm text-stone-400 mb-6">40 tokens every month</div>
            <ul className="text-sm text-stone-600 space-y-2 mb-6 text-left flex-grow">
              <li>40 full resume passes per month</li>
              <li>Or 400 inline edits</li>
              <li>Auto-refills so you never run out mid-hunt</li>
              <li>Cancel anytime, no questions asked</li>
            </ul>
            <Link
              to="/login"
              className="w-full bg-orange-600 text-white py-2.5 rounded-lg hover:bg-orange-700 font-medium min-h-[44px] mt-auto text-center inline-flex items-center justify-center"
            >
              Subscribe
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-stone-900 py-16">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">One resume per job. Thirty seconds each.</h2>
          <p className="text-stone-300 text-lg mb-8">The AI writes the draft. You keep the final say.</p>
          <Link
            to="/app"
            className="bg-orange-600 text-white text-lg px-8 py-4 rounded-xl hover:bg-orange-700 transition-colors font-semibold shadow-lg min-h-[44px] inline-flex items-center justify-center"
          >
            Try it free, no signup needed
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-stone-200 py-8 bg-stone-50">
        <div className="max-w-6xl mx-auto px-4 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="DraftResume" className="h-6 w-auto" />
            <span className="text-sm text-stone-500">DraftResume</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-stone-400">
            <a href="/privacy" className="hover:text-stone-600">Privacy</a>
            <span>GKCG, LLC</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
